import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  InputAdornment,
  Stack,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  Checkbox,
} from '@mui/material';
import { Visibility, VisibilityOff, PersonAddOutlined, CloudUploadOutlined, CheckCircle, Cancel } from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api';
import { uiPT } from '../i18n';
import { cleanWhatsAppNumber, validateWhatsAppNumber } from '../utils/phoneUtils';
import { MuiTelInput } from 'mui-tel-input';
import Card from '../components/Card';

interface FormData {
  name: string;
  type: string;
  modality: string;
  phoneNumber: string;
  IBAN: string;
  identityType: string;
  city: string;
  idCardType: 'pdf' | 'image';
  selfie?: File;
  modalityPhoto?: File;
  driverLicense?: File;
  idCardFront?: File;
  idCardBack?: File;
  idCardPdf?: File;
  caeDocument?: File;
  taxClearanceDocument?: File;
  termsAccepted: boolean;
}

const schema = yup.object({
  name: yup.string().required('Nome é obrigatório').min(2, 'Nome deve ter pelo menos 2 caracteres'),
  type: yup.string().required('Tipo é obrigatório').oneOf(['courier', 'driver'], 'Tipo inválido'),
  modality: yup.string().required('Modalidade é obrigatória').oneOf(['bike', 'motorcycle'], 'Modalidade inválida'),
  phoneNumber: yup.string().required('Telefone WhatsApp é obrigatório').test('whatsapp-validation', 'Número WhatsApp deve ser um número internacional válido (ex: +351 912 345 678, +1 555 123 4567)', (value) => validateWhatsAppNumber(value || '')),
  IBAN: yup.string()
    .required('IBAN é obrigatório')
    .transform((value) => value ? value.replace(/\s/g, '') : value) // Remove spaces for validation
    .matches(/^[A-Z]{2}[0-9]{2}[A-Z0-9]{4}[0-9]{7}([A-Z0-9]?){0,16}$/, 'IBAN inválido'),
  identityType: yup.string().required('Tipo de identidade é obrigatório').oneOf(['passport', 'cc', 'trc', 'cplp'], 'Tipo de identidade inválido'),
  city: yup.string().required('Cidade é obrigatória').min(2, 'Cidade deve ter pelo menos 2 caracteres'),
  idCardType: yup.string().required('Tipo de documento é obrigatório').oneOf(['pdf', 'image'], 'Tipo inválido'),
  selfie: yup.mixed().required('Selfie é obrigatória'),
  modalityPhoto: yup.mixed().required('Foto da mochila é obrigatória'),
  driverLicense: yup.mixed().when('modality', {
    is: 'motorcycle',
    then: (schema) => schema.required('Carta de condução é obrigatória para motocicleta'),
    otherwise: (schema) => schema.optional(),
  }),
  idCardFront: yup.mixed().when('idCardType', {
    is: 'image',
    then: (schema) => schema.required('Frente do CC/TR/CPLP é obrigatória'),
    otherwise: (schema) => schema.optional(),
  }),
  idCardBack: yup.mixed().when('idCardType', {
    is: 'image',
    then: (schema) => schema.required('Verso do CC/TR/CPLP é obrigatório'),
    otherwise: (schema) => schema.optional(),
  }),
  idCardPdf: yup.mixed().when('idCardType', {
    is: 'pdf',
    then: (schema) => schema.required('CC/TR/CPLP em PDF é obrigatório'),
    otherwise: (schema) => schema.optional(),
  }),
  caeDocument: yup.mixed().required('Código de atividade económica (CAE) é obrigatório'),
  taxClearanceDocument: yup.mixed().required('Certidão de Não Dívida - Autoridade Tributária é obrigatória'),
  termsAccepted: yup.boolean().oneOf([true], 'Você deve aceitar os termos e dados'),
}) as yup.ObjectSchema<FormData>;

export default function RegisterPage() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [trackingCode, setTrackingCode] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, string>>({});

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      type: '',
      modality: '',
      phoneNumber: '',
      IBAN: '',
      identityType: '',
      city: '',
      idCardType: 'image',
      termsAccepted: false,
    },
  });

  const modality = watch('modality');
  const idCardType = watch('idCardType');

  // IBAN mask function
  const formatIBAN = (value: string) => {
    // Remove all non-alphanumeric characters
    const cleaned = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    
    // Apply IBAN format with spaces every 4 characters
    const formatted = cleaned.replace(/(.{4})/g, '$1 ').trim();
    
    return formatted;
  };

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    setTrackingCode(null);

    try {
      const formData = new FormData();
      
      // Add form fields
      formData.append('name', data.name);
      formData.append('type', data.type);
      formData.append('modality', data.modality);
      formData.append('phoneNumber', cleanWhatsAppNumber(data.phoneNumber));
      formData.append('IBAN', data.IBAN.replace(/\s/g, '')); // Remove spaces before sending
      formData.append('identityType', data.identityType);
      formData.append('city', data.city);
      formData.append('idCardType', data.idCardType);

      // Add file fields
      if (data.selfie) formData.append('selfie', data.selfie);
      if (data.modalityPhoto) formData.append('modalityPhoto', data.modalityPhoto);
      if (data.driverLicense) formData.append('driverLicense', data.driverLicense);
      if (data.idCardFront) formData.append('idCardFront', data.idCardFront);
      if (data.idCardBack) formData.append('idCardBack', data.idCardBack);
      if (data.idCardPdf) formData.append('idCardPdf', data.idCardPdf);
      if (data.caeDocument) formData.append('caeDocument', data.caeDocument);
      if (data.taxClearanceDocument) formData.append('taxClearanceDocument', data.taxClearanceDocument);

      const res = await api.post('/client/register', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      setSuccess(uiPT.register.success);
      setTrackingCode(res.data.data.trackingCode);
    } catch (err: any) {
      setError(uiPT.register.error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (field: keyof FormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setValue(field, file);
      setUploadedFiles(prev => ({
        ...prev,
        [field]: file.name
      }));
    }
  };

  const removeFile = (field: keyof FormData) => {
    setValue(field, undefined);
    setUploadedFiles(prev => {
      const newFiles = { ...prev };
      delete newFiles[field];
      return newFiles;
    });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: (theme) => theme.palette.grey[100],
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Card sx={{ p: 4 }}>
          <Stack spacing={4} alignItems="center">
            {/* Header */}
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 2,
                  background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                  boxShadow: (theme) => theme.shadows[3],
                }}
              >
                <PersonAddOutlined sx={{ color: 'white', fontSize: 32 }} />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                {uiPT.register.title}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Preencha todos os dados para registrar sua solicitação
              </Typography>
            </Box>

            {success && trackingCode ? (
              <Alert 
                severity="success" 
                sx={{ 
                  mt: 3, 
                  borderRadius: 2,
                  '& .MuiAlert-icon': {
                    color: '#2e7d32',
                  },
                }}
              >
                {success} <strong>{trackingCode}</strong>
              </Alert>
            ) : (
              <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Controller
                      name="name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label={uiPT.register.name}
                          error={!!errors.name}
                          helperText={errors.name?.message}
                          required
                          sx={{
                            flex: 1,
                            minWidth: 250,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '&:hover fieldset': {
                                borderColor: '#667eea',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#667eea',
                              },
                            },
                          }}
                        />
                      )}
                    />
                    <Controller
                      name="type"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          label={uiPT.register.type}
                          error={!!errors.type}
                          helperText={errors.type?.message}
                          required
                          sx={{
                            flex: 1,
                            minWidth: 250,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '&:hover fieldset': {
                                borderColor: '#667eea',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#667eea',
                              },
                            },
                          }}
                        >
                          <MenuItem value="courier">{uiPT.register.courier}</MenuItem>
                          <MenuItem value="driver">{uiPT.register.driver}</MenuItem>
                        </TextField>
                      )}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Controller
                      name="modality"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          label={uiPT.register.modality}
                          error={!!errors.modality}
                          helperText={errors.modality?.message}
                          required
                          sx={{
                            flex: 1,
                            minWidth: 250,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '&:hover fieldset': {
                                borderColor: '#667eea',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#667eea',
                              },
                            },
                          }}
                        >
                          <MenuItem value="bike">{uiPT.register.bike}</MenuItem>
                          <MenuItem value="motorcycle">{uiPT.register.motorcycle}</MenuItem>
                        </TextField>
                      )}
                    />
                    <Controller
                      name="phoneNumber"
                      control={control}
                      render={({ field }) => (
                        <MuiTelInput
                          {...field}
                          label="Telefone WhatsApp"
                          placeholder="+351 912 345 678"
                          error={!!errors.phoneNumber}
                          helperText={errors.phoneNumber?.message}
                          required
                          defaultCountry="PT"
                          preferredCountries={['PT', 'BR', 'US', 'GB', 'FR', 'ES', 'DE', 'IT']}
                          sx={{
                            flex: 1,
                            minWidth: 250,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '&:hover fieldset': {
                                borderColor: '#667eea',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#667eea',
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Controller
                      name="IBAN"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label={uiPT.register.IBAN}
                          placeholder="PT50 1234 5678 9012 3456 7890 1"
                          error={!!errors.IBAN}
                          helperText={errors.IBAN?.message}
                          required
                          onChange={(e) => {
                            const formatted = formatIBAN(e.target.value);
                            console.log(formatted);
                            field.onChange(formatted);
                          }}
                          sx={{
                            flex: 1,
                            minWidth: 250,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '&:hover fieldset': {
                                borderColor: '#667eea',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#667eea',
                              },
                            },
                          }}
                        />
                      )}
                    />
                    <Controller
                      name="identityType"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          label="Tipo de Identidade"
                          error={!!errors.identityType}
                          helperText={errors.identityType?.message}
                          required
                          sx={{
                            flex: 1,
                            minWidth: 250,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '&:hover fieldset': {
                                borderColor: '#667eea',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#667eea',
                              },
                            },
                          }}
                        >
                          <MenuItem value="passport">Passaporte</MenuItem>
                          <MenuItem value="cc">Cartão de cidadão (CC)</MenuItem>
                          <MenuItem value="trc">Autorização de residência (TRC)</MenuItem>
                          <MenuItem value="cplp">CPLP</MenuItem>
                        </TextField>
                      )}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Controller
                      name="city"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label={uiPT.register.city}
                          error={!!errors.city}
                          helperText={errors.city?.message}
                          required
                          sx={{
                            flex: 1,
                            minWidth: 250,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '&:hover fieldset': {
                                borderColor: '#667eea',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#667eea',
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Box>

                  {/* ID Card Type Selection */}
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2, color: '#2c3e50', fontWeight: 600 }}>
                      Tipo de Documento de Identidade
                    </Typography>
                    <Controller
                      name="idCardType"
                      control={control}
                      render={({ field }) => (
                        <FormControl component="fieldset" error={!!errors.idCardType}>
                          <FormLabel component="legend" sx={{ color: '#2c3e50', fontWeight: 600, mb: 1 }}>
                            Escolha o formato do seu documento:
                          </FormLabel>
                          <RadioGroup
                            {...field}
                            row
                            sx={{
                              '& .MuiFormControlLabel-root': {
                                marginRight: 3,
                              },
                              '& .MuiRadio-root': {
                                color: '#667eea',
                                '&.Mui-checked': {
                                  color: '#667eea',
                                },
                              },
                            }}
                          >
                            <FormControlLabel
                              value="image"
                              control={<Radio />}
                              label="Imagens (Frente e Verso)"
                            />
                            <FormControlLabel
                              value="pdf"
                              control={<Radio />}
                              label="PDF"
                            />
                          </RadioGroup>
                          {errors.idCardType && (
                            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                              {errors.idCardType.message}
                            </Typography>
                          )}
                        </FormControl>
                      )}
                    />
                  </Box>

                  {/* Document Upload Section */}
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2, color: '#2c3e50', fontWeight: 600 }}>
                      Documentos Necessários
                    </Typography>
                  </Box>

                  {/* ID Card Documents */}
                  <Box>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#2c3e50' }}>
                      Documento de Identidade
                    </Typography>
                    <Stack spacing={2}>
                      {idCardType === 'image' ? (
                        <>
                          <Card variant="outlined" sx={{ p: 3 }}>
                            <Stack spacing={2}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                  {uiPT.register.idCardFront}
                                </Typography>
                                <Chip label="Obrigatório" color="error" size="small" />
                              </Box>
                              <Button
                                component="label"
                                variant="outlined"
                                startIcon={<CloudUploadOutlined />}
                                fullWidth
                                sx={{ justifyContent: 'center' }}
                              >
                                {uiPT.resubmit.selectFile}
                                <input
                                  type="file"
                                  accept="image/*"
                                  hidden
                                  onChange={handleFileChange('idCardFront')}
                                  required={idCardType === 'image'}
                                />
                              </Button>
                              {uploadedFiles.idCardFront && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
                                  <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                                  <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, flexGrow: 1 }}>
                                    {uploadedFiles.idCardFront}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => removeFile('idCardFront')}
                                    sx={{ color: 'error.main' }}
                                  >
                                    <Cancel sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Box>
                              )}
                              {errors.idCardFront && (
                                <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                                  {errors.idCardFront.message}
                                </Typography>
                              )}
                            </Stack>
                          </Card>

                          <Card variant="outlined" sx={{ p: 3 }}>
                            <Stack spacing={2}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                  {uiPT.register.idCardBack}
                                </Typography>
                                <Chip label="Obrigatório" color="error" size="small" />
                              </Box>
                              <Button
                                component="label"
                                variant="outlined"
                                startIcon={<CloudUploadOutlined />}
                                fullWidth
                                sx={{ justifyContent: 'center' }}
                              >
                                {uiPT.resubmit.selectFile}
                                <input
                                  type="file"
                                  accept="image/*"
                                  hidden
                                  onChange={handleFileChange('idCardBack')}
                                  required={idCardType === 'image'}
                                />
                              </Button>
                              {uploadedFiles.idCardBack && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
                                  <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                                  <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, flexGrow: 1 }}>
                                    {uploadedFiles.idCardBack}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => removeFile('idCardBack')}
                                    sx={{ color: 'error.main' }}
                                  >
                                    <Cancel sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Box>
                              )}
                              {errors.idCardBack && (
                                <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                                  {errors.idCardBack.message}
                                </Typography>
                              )}
                            </Stack>
                          </Card>
                        </>
                      ) : (
                        <Card variant="outlined" sx={{ p: 3 }}>
                          <Stack spacing={2}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {uiPT.register.idCardPdf}
                              </Typography>
                              <Chip label="Obrigatório" color="error" size="small" />
                            </Box>
                            <Button
                              component="label"
                              variant="outlined"
                              startIcon={<CloudUploadOutlined />}
                              fullWidth
                              sx={{ justifyContent: 'center' }}
                            >
                              {uiPT.resubmit.selectFile}
                              <input
                                type="file"
                                accept="application/pdf"
                                hidden
                                onChange={handleFileChange('idCardPdf')}
                                required={idCardType === 'pdf'}
                              />
                            </Button>
                            {uploadedFiles.idCardPdf && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
                                <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                                <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, flexGrow: 1 }}>
                                  {uploadedFiles.idCardPdf}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => removeFile('idCardPdf')}
                                  sx={{ color: 'error.main' }}
                                >
                                  <Cancel sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Box>
                            )}
                            {errors.idCardPdf && (
                              <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                                {errors.idCardPdf.message}
                              </Typography>
                            )}
                          </Stack>
                        </Card>
                      )}
                    </Stack>
                  </Box>

                  {/* Other Documents */}
                  <Box>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#2c3e50' }}>
                      Outros Documentos
                    </Typography>
                    <Stack spacing={2}>
                      <Card variant="outlined" sx={{ p: 3 }}>
                        <Stack spacing={2}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {uiPT.register.selfie}
                            </Typography>
                            <Chip label="Obrigatório" color="error" size="small" />
                          </Box>
                          <Button
                            component="label"
                            variant="outlined"
                            startIcon={<CloudUploadOutlined />}
                            fullWidth
                            sx={{ justifyContent: 'center' }}
                          >
                            {uiPT.resubmit.selectFile}
                            <input
                              type="file"
                              accept="image/*"
                              hidden
                              onChange={handleFileChange('selfie')}
                              required
                            />
                          </Button>
                          {uploadedFiles.selfie && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
                              <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                              <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, flexGrow: 1 }}>
                                {uploadedFiles.selfie}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => removeFile('selfie')}
                                sx={{ color: 'error.main' }}
                              >
                                <Cancel sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Box>
                          )}
                          {errors.selfie && (
                            <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                              {errors.selfie.message}
                            </Typography>
                          )}
                        </Stack>
                      </Card>

                      <Card variant="outlined" sx={{ p: 3 }}>
                        <Stack spacing={2}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {uiPT.register.modalityPhoto}
                            </Typography>
                            <Chip label="Obrigatório" color="error" size="small" />
                          </Box>
                          <Button
                            component="label"
                            variant="outlined"
                            startIcon={<CloudUploadOutlined />}
                            fullWidth
                            sx={{ justifyContent: 'center' }}
                          >
                            {uiPT.resubmit.selectFile}
                            <input
                              type="file"
                              accept="image/*"
                              hidden
                              onChange={handleFileChange('modalityPhoto')}
                              required
                            />
                          </Button>
                          {uploadedFiles.modalityPhoto && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
                              <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                              <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, flexGrow: 1 }}>
                                {uploadedFiles.modalityPhoto}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => removeFile('modalityPhoto')}
                                sx={{ color: 'error.main' }}
                              >
                                <Cancel sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Box>
                          )}
                          {errors.modalityPhoto && (
                            <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                              {errors.modalityPhoto.message}
                            </Typography>
                          )}
                        </Stack>
                      </Card>

                      {modality === 'motorcycle' && (
                        <Card variant="outlined" sx={{ p: 3 }}>
                          <Stack spacing={2}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {uiPT.register.driverLicense}
                              </Typography>
                              <Chip label="Obrigatório" color="error" size="small" />
                            </Box>
                            <Button
                              component="label"
                              variant="outlined"
                              startIcon={<CloudUploadOutlined />}
                              fullWidth
                              sx={{ justifyContent: 'center' }}
                            >
                              {uiPT.resubmit.selectFile}
                              <input
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={handleFileChange('driverLicense')}
                                required={modality === 'motorcycle'}
                              />
                            </Button>
                            {uploadedFiles.driverLicense && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
                                <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                                <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, flexGrow: 1 }}>
                                  {uploadedFiles.driverLicense}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => removeFile('driverLicense')}
                                  sx={{ color: 'error.main' }}
                                >
                                  <Cancel sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Box>
                            )}
                            {errors.driverLicense && (
                              <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                                {errors.driverLicense.message}
                              </Typography>
                            )}
                          </Stack>
                        </Card>
                      )}

                      {/* CAE Document */}
                      <Card variant="outlined" sx={{ p: 3 }}>
                        <Stack spacing={2}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {uiPT.register.caeDocument}
                            </Typography>
                            <Chip label="Obrigatório" color="error" size="small" />
                          </Box>
                          <Button
                            component="label"
                            variant="outlined"
                            startIcon={<CloudUploadOutlined />}
                            fullWidth
                            sx={{ justifyContent: 'center' }}
                          >
                            {uiPT.resubmit.selectFile}
                            <input
                              type="file"
                              accept="application/pdf"
                              hidden
                              onChange={handleFileChange('caeDocument')}
                              required
                            />
                          </Button>
                          {uploadedFiles.caeDocument && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
                              <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                              <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, flexGrow: 1 }}>
                                {uploadedFiles.caeDocument}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => removeFile('caeDocument')}
                                sx={{ color: 'error.main' }}
                              >
                                <Cancel sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Box>
                          )}
                          {errors.caeDocument && (
                            <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                              {errors.caeDocument.message}
                            </Typography>
                          )}
                        </Stack>
                      </Card>

                      {/* Tax Clearance Document */}
                      <Card variant="outlined" sx={{ p: 3 }}>
                        <Stack spacing={2}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {uiPT.register.taxClearanceDocument}
                            </Typography>
                            <Chip label="Obrigatório" color="error" size="small" />
                          </Box>
                          <Button
                            component="label"
                            variant="outlined"
                            startIcon={<CloudUploadOutlined />}
                            fullWidth
                            sx={{ justifyContent: 'center' }}
                          >
                            {uiPT.resubmit.selectFile}
                            <input
                              type="file"
                              accept="application/pdf"
                              hidden
                              onChange={handleFileChange('taxClearanceDocument')}
                              required
                            />
                          </Button>
                          {uploadedFiles.taxClearanceDocument && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
                              <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                              <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, flexGrow: 1 }}>
                                {uploadedFiles.taxClearanceDocument}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => removeFile('taxClearanceDocument')}
                                sx={{ color: 'error.main' }}
                              >
                                <Cancel sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Box>
                          )}
                          {errors.taxClearanceDocument && (
                            <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                              {errors.taxClearanceDocument.message}
                            </Typography>
                          )}
                        </Stack>
                      </Card>
                    </Stack>
                  </Box>
                </Box>

                {error && (
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mt: 3, 
                      borderRadius: 2,
                      '& .MuiAlert-icon': {
                        color: '#d32f2f',
                      },
                    }}
                  >
                    {error}
                  </Alert>
                )}
                {success && (
                  <Alert 
                    severity="success" 
                    sx={{ 
                      mt: 3, 
                      borderRadius: 2,
                      '& .MuiAlert-icon': {
                        color: '#2e7d32',
                      },
                    }}
                  >
                    {success} <strong>{trackingCode}</strong>
                  </Alert>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  sx={{
                    mt: 3,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "1.1rem",
                    fontWeight: 600,
                  }}
                >
                  {loading ? "Registrando..." : "Registrar"}
                </Button>

                {/* Terms and Data Consent */}
                <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Controller
                    name="termsAccepted"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            {...field}
                            checked={field.value}
                            sx={{
                              color: '#667eea',
                              '&.Mui-checked': {
                                color: '#667eea',
                              },
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            Autorizo o uso dos meus dados e documentos exclusivamente para fins de criação e ativação de conta na plataforma Uber.
                          </Typography>
                        }
                        sx={{ alignItems: 'flex-start' }}
                      />
                    )}
                  />
                  {errors.termsAccepted && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: "#FF5630" }}>
                      {errors.termsAccepted.message}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Stack>
        </Card>
      </Container>
    </Box>
  );
} 